import { useEffect, useMemo, useState } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from './Toast';
import { ApiError } from '../api/types';
import { theme } from '../theme';
import { Icon } from './Icon';
import { fmtK, fmtMoney, todayIso } from '../utils/format';
import { computeMonthBudget } from '../utils/month-budget';
import { oneOffIdsFrom } from '../utils/oneOff';
import { reserveSets } from '../utils/reserveState';
import {
  computeRecordingStreak,
  readBestStreak,
  recordedDaySet,
  saveBestStreak,
} from '../utils/recording-streak';
import { coinsBalance, freezeInventory, frozenDates, monthKeyOf, toIso, weekDays, weekMonday } from '../utils/game';
import { computeHotCategoryStreak } from '../utils/streaks';

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

/**
 * «Обложка» дашборда: один мощный фокус — остаток дня гигантским Didot
 * по центру холста, тонкая строка статуса с line-иконками.
 */
export function DayCard() {
  const { viewData, addGameEvent } = useBudget();
  const toast = useToast();
  const nowDate = new Date();
  const monthKey = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, '0')}`;
  const [busy, setBusy] = useState(false);
  // Рекорд стрика читаем один раз при монтировании — расчёт остаётся чистым.
  const [bestSoFar] = useState(readBestStreak);

  const view = useMemo(() => {
    if (!viewData) return null;
    const today = todayIso();
    const oneOffIds = oneOffIdsFrom(viewData.operations);
    const { released } = reserveSets(viewData, monthKey);
    const mb = computeMonthBudget(viewData.operations, viewData.categories, viewData.settings, new Date(), oneOffIds, released);
    const dailyBudget = mb.active ? mb.dynamicDailyLimit : mb.staticDailyLimit;

    const catByName = new Map(viewData.categories.map((c) => [c.name, c] as const));
    let expense = 0;
    let bigExpense = 0;
    let income = 0;
    for (const op of viewData.operations) {
      if (op.date !== today) continue;
      const cat = catByName.get(op.category);
      const t = cat?.type ?? op.type;
      if (t === 'Расход') {
        if (cat && !cat.is_daily) bigExpense += op.amount;
        else expense += op.amount;
      } else if (t === 'Доход') income += op.amount;
    }

    const frozen = frozenDates(viewData.game_events ?? []);
    const streak = computeRecordingStreak(
      viewData.operations,
      frozen,
      new Date(),
      viewData.aggregates,
      bestSoFar,
    );
    const coins = coinsBalance(viewData);
    const freezes = freezeInventory(viewData.game_events ?? []);
    const hot = computeHotCategoryStreak(
      viewData.operations,
      viewData.categories,
      new Date(),
      viewData.settings.stats_since,
      viewData.aggregates,
    );

    const recorded = recordedDaySet(viewData.operations, viewData.aggregates);
    const now = new Date();
    const yesterday = toIso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
    const dayBefore = toIso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2));
    const canRepair =
      freezes > 0 &&
      !recorded.has(yesterday) &&
      !frozen.has(yesterday) &&
      (recorded.has(dayBefore) || frozen.has(dayBefore));

    // Линейка недели для моста «день → неделя»
    const WEEK_LABELS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
    const week = weekDays(weekMonday(now)).map((d, i) => ({
      label: WEEK_LABELS[i],
      state: (d === today ? 'today' : d < today ? 'past' : 'future') as 'today' | 'past' | 'future',
    }));

    // Streak-chain: последние 14 дней как засечки (семантика хитмапа)
    const chain: { h: number; color: string }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = toIso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - i));
      if (i === 0) chain.push({ h: 14, color: theme.colors.ink });
      else if (frozen.has(d)) chain.push({ h: 10, color: theme.colors.frostInk });
      else if (recorded.has(d)) chain.push({ h: 10, color: theme.colors.ink });
      else chain.push({ h: 5, color: theme.colors.greenSoft });
    }

    return {
      remaining: dailyBudget - expense,
      dailyBudget, expense, bigExpense, income,
      streak, coins, freezes, hot, mb, canRepair, yesterday, chain, week,
    };
  }, [viewData, monthKey, bestSoFar]);

  // Рекорд стрика — побочный эффект, ему не место в расчёте (TODO.md §7).
  const streakCurrent = view?.streak.current ?? 0;
  useEffect(() => { saveBestStreak(streakCurrent); }, [streakCurrent]);

  if (!view || !viewData) return null;

  const currency = viewData.settings.currency ?? '₽';
  const now = new Date();
  const dateLabel = `${WEEKDAYS[now.getDay()]}, ${now.getDate()} ${MONTHS_GEN[now.getMonth()]}`;
  const positive = view.remaining >= 0;
  const amountColor = positive ? theme.colors.ink : theme.colors.accent;

  async function repair() {
    if (!view) return;
    setBusy(true);
    try {
      const r = await addGameEvent({
        id: `frz_${view.yesterday}`,
        type: 'freeze_use',
        month: monthKeyOf(new Date()),
        week: view.yesterday,
        coins: 0,
        payload: JSON.stringify({ freeze: -1 }),
      });
      toast.show(`Вчера закрыто заморозкой — стрик жив${r.synced ? '' : ' · синх позже'}`);
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <header style={{ padding: '6px 0 22px', overflow: 'hidden' }}>
      {/* верхняя строка: дата · streak-chain (засечки 14 дней, семантика хитмапа) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="caption">Сегодня · {dateLabel}</span>
        <span
          title={`цепь ${view.streak.current} дней`}
          style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2.5 }}
        >
          {view.chain.map((c, i) => (
            <span key={i} style={{ width: 3, height: c.h, background: c.color }} />
          ))}
          <span
            style={{
              fontSize: 11,
              color: theme.colors.sub,
              marginLeft: 5,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {view.streak.current}
          </span>
        </span>
      </div>

      {/* кадрированный герой-датум */}
      <div className="caption" style={{ marginTop: 16 }}>
        {positive ? 'можно сегодня' : 'сверх лимита'}
      </div>
      <div
        className="num"
        style={{
          fontSize: 'clamp(52px, 19vw, 84px)',
          lineHeight: 0.9,
          letterSpacing: '-0.02em',
          marginLeft: '-0.02em',
          marginTop: 6,
          color: amountColor,
        }}
      >
        {positive ? '' : '−'}
        {Math.round(Math.abs(view.remaining)).toLocaleString('ru-RU')}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginTop: 12,
          gap: 12,
        }}
      >
        <span style={{ fontStyle: 'italic', color: theme.colors.sub, fontSize: 16, lineHeight: 1.4 }}>
          из {fmtMoney(view.dailyBudget, currency)} дневного пула
          {view.mb.active && <> · цель {fmtK(view.mb.monthTarget)} под защитой</>}
        </span>
        {view.expense > 0 && (
          <span className="num" style={{ fontSize: 22, color: theme.colors.accent, whiteSpace: 'nowrap' }}>
            −{fmtK(view.expense)}
          </span>
        )}
      </div>

      {(view.bigExpense > 0 || view.income > 0 || view.hot) && (
        <div style={{ fontSize: 13, color: theme.colors.sub, marginTop: 8 }}>
          {view.income > 0 && <>доход +{fmtK(view.income)} · </>}
          {view.bigExpense > 0 && <>крупные −{fmtK(view.bigExpense)} вне лимита · </>}
          {view.hot && <>{view.hot.days} дн. без «{view.hot.category}»</>}
        </div>
      )}

      {view.canRepair && (
        <button
          disabled={busy}
          onClick={repair}
          style={{
            marginTop: 16,
            padding: '9px 16px',
            background: 'transparent',
            color: theme.colors.ink,
            border: `1px solid ${theme.colors.ink}`,
            borderRadius: theme.radius.sm,
            fontFamily: theme.font,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <Icon name="snow" size={14} inline /> закрыть вчерашний пропуск заморозкой
        </button>
      )}

      {/* мост «день → неделя»: линейка недели с засечками вместо стрелки */}
      {view.mb.weekSafeToSpend > 0 && (
        <div style={{ marginTop: 20, borderTop: `1px solid ${theme.colors.line}`, paddingTop: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span className="caption">неделя</span>
            <span className="num" style={{ fontSize: 22 }}>
              {fmtMoney(view.mb.weekSafeToSpend, currency)}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            {view.week.map((d) => (
              <div key={d.label} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ height: 16, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                  {d.state === 'future' ? (
                    <span style={{ width: 2, height: 12, borderLeft: `2px solid ${theme.colors.subSoft}` }} />
                  ) : (
                    <span
                      style={{
                        width: d.state === 'today' ? 3 : 2,
                        height: d.state === 'today' ? 16 : 8,
                        background: d.state === 'today' ? theme.colors.ink : theme.colors.sub,
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: d.state === 'today' ? theme.colors.ink : theme.colors.sub,
                    marginTop: 3,
                  }}
                >
                  {d.label}
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic', marginTop: 8 }}>
            пул делится на остаток недели ≈{fmtK(view.mb.weekSafeToSpend / view.mb.daysLeftThisWeek)}/день
          </div>
        </div>
      )}
    </header>
  );
}
